import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, usePage } from '@inertiajs/react';
import { cn } from '../../lib/cn';
import ThemeToggle from '../ui/ThemeToggle';
import LanguageToggle from '../ui/LanguageToggle';
import ImpersonationBanner from './ImpersonationBanner';
import Sidebar, { getTeacherNavItems } from './Sidebar';
import { useScrollTitle } from '../../hooks/useScrollTitle';
import { useUserNotifications } from '../../hooks/useUserNotifications';
import { truncateWords } from '../../lib/truncateWords';
import Logo from '../ui/Logo';
import InviteIsland from '../ui/InviteIsland';
import BreadcrumbBar from '../navigation/BreadcrumbBar';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { NavIcon, resolveNavIconKey } from './Sidebar';

export default function TeacherShell({
  children,
  maxWidth = 'default',
  className,
  scrollTitle: scrollTitleText,
  forceShowScrollTitle = false,
  footer,
}) {
  const { auth, flash } = usePage().props;
  const { t } = useTranslation();
  const teacher = auth?.user;
  const navItems = getTeacherNavItems(teacher?.role, t);
  const { scrollRef, visibleTitle } = useScrollTitle(scrollTitleText);
  const effectiveScrollTitle = forceShowScrollTitle ? scrollTitleText : visibleTitle;
  const { invite, dismissInvite } = useUserNotifications(teacher?.id);

  const maxWidths = {
    narrow: 'max-w-3xl',
    default: 'max-w-7xl',
    wide: 'max-w-screen-2xl',
    full: 'max-w-full',
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      <ImpersonationBanner />
      <InviteIsland invite={invite} onDismiss={dismissInvite} />
      <div className="flex h-screen overflow-hidden">
        <Sidebar items={navItems} />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <TopBar teacher={teacher} scrollTitle={effectiveScrollTitle} />
          <main
            ref={scrollRef}
            className={cn(
              'flex-1 overflow-y-auto px-6 py-8',
              maxWidths[maxWidth],
              maxWidth !== 'full' && 'mx-auto w-full',
              className
            )}
          >
            <FlashMessages flash={flash} />
            {children}
          </main>
          {footer && (
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Pages on the vertical-nav axis (book → chapter → passage → sentence → word).
// The BreadcrumbBar only renders when we're on one of these — otherwise stale
// reader context would pollute unrelated pages.
const VERTICAL_NAV_URL = /^\/(?:teacher|student)?\/?(?:library|dictionary|sentence_studio|morphemes)(?:\/|$)/;

function TopBar({ teacher, scrollTitle }) {
  const { t } = useTranslation();
  const { crumbs } = useBreadcrumbs();
  const { url } = usePage();
  const onVerticalNav = VERTICAL_NAV_URL.test(url || '');
  const hasCrumbs = onVerticalNav && crumbs && crumbs.length > 0;
  const iconKey = resolveNavIconKey(url, teacher?.role);

  return (
    <header className="flex-shrink-0 h-14 bg-white/80 backdrop-blur-md border-b border-gray-200 dark:bg-gray-900/80 dark:border-gray-700 z-30">
      <div className="flex items-center justify-between h-full px-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Mobile hamburger is rendered inside Sidebar via MobileMenuButton */}
          <Link href="/teacher/dashboard" className="flex items-center gap-2 flex-shrink-0">
            <Logo className="h-9" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded hidden sm:inline">
              {t('nav.teacher')}
            </span>
          </Link>

          {hasCrumbs ? (
            <div className="pl-3 border-l border-gray-400 dark:border-gray-400 min-w-0 flex items-center gap-2">
              {iconKey && (
                <NavIcon
                  iconKey={iconKey}
                  className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
                />
              )}
              <BreadcrumbBar />
            </div>
          ) : (
            /* Animated scroll title — merges in when page header scrolls out of view */
            <div
              className={cn(
                'overflow-hidden transition-all duration-300 ease-out min-w-0',
                scrollTitle ? 'max-w-md opacity-100' : 'max-w-0 opacity-0'
              )}
            >
              <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2 pl-3 border-l border-gray-400 dark:border-gray-400">
                {iconKey && (
                  <NavIcon
                    iconKey={iconKey}
                    className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
                  />
                )}
                <span className="block truncate">
                  {typeof scrollTitle === 'string' ? truncateWords(scrollTitle) : scrollTitle}
                </span>
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <ThemeToggle />
          {teacher ? (
            <UserMenu user={teacher} />
          ) : (
            <Link
              href="/login"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
            >
              {t('nav.logIn')}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function UserMenu({ user }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 focus:outline-none"
      >
        <div className="hidden sm:block text-right">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {user.full_name}
          </p>
        </div>
        <div
          className={cn(
            'w-8 h-8 rounded-full',
            'bg-gradient-to-br from-gray-700 to-gray-900 dark:from-gray-400 dark:to-gray-600',
            'flex items-center justify-center',
            'hover:ring-2 hover:ring-gray-400 dark:hover:ring-gray-500 transition-all'
          )}
        >
          <span className="text-xs font-medium text-white dark:text-gray-900">{user.initials}</span>
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {user.full_name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
          </div>
          <Link
            href="/teacher/profile"
            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={() => setIsOpen(false)}
          >
            {t('nav.profile')}
          </Link>
          <div className="border-t border-gray-100 dark:border-gray-700">
            <LanguageToggle />
          </div>
          <Link
            href="/logout"
            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-t border-gray-100 dark:border-gray-700"
            onClick={() => setIsOpen(false)}
          >
            {t('nav.logOut')}
          </Link>
        </div>
      )}
    </div>
  );
}

function FlashMessages({ flash }) {
  const [dismissed, setDismissed] = useState({});

  if (!flash) return null;

  const messages = [];
  if (flash.notice && !dismissed.notice) {
    messages.push({ type: 'notice', text: flash.notice });
  }
  if (flash.alert && !dismissed.alert) {
    messages.push({ type: 'alert', text: flash.alert });
  }

  if (messages.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {messages.map(({ type, text }) => (
        <div
          key={type}
          className={cn(
            'px-4 py-3 rounded-lg text-sm flex items-center justify-between',
            type === 'alert'
              ? 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
              : 'bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
          )}
        >
          <span>{text}</span>
          <button
            onClick={() => setDismissed(prev => ({ ...prev, [type]: true }))}
            className="ml-3 flex-shrink-0 text-current opacity-50 hover:opacity-100"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

export function TeacherPageHeader({
  title,
  subtitle,
  backLink,
  backLabel = 'Back',
  actions,
  className,
}) {
  return (
    <div className={cn('mb-8', className)}>
      {backLink && (
        <Link
          href={backLink}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 transition-colors duration-200"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {backLabel}
        </Link>
      )}
      <div className="flex items-start justify-between">
        <div>
          <h1 data-testid="page-header-title" className="text-3xl font-bold text-gray-900 dark:text-white">{title}</h1>
          {subtitle && (
            <p className="mt-2 text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-3">{actions}</div>
        )}
      </div>
    </div>
  );
}
